import SettingsIcon from '@assets/icons/settings.svg?react'
import { MMButton } from '@components'
import cn from 'classnames'
import { useDropDownMenu } from 'mmfintech-commons'
import { type CSSProperties, type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'

import './EntityRowActions.scss'

export type RowAction = {
  key: string
  label: string
  icon: ReactNode
  menuIcon: ReactNode
  onClick: () => void
  testId: string
  showInDesktop?: boolean
  showInMenu?: boolean
}

interface Props {
  actions: RowAction[]
  compact: boolean
  /** Caller must mount a matching <ReactTooltip id={...}>. */
  tooltipId: string
  menuTestId: string
}

export const EntityRowActions = ({ actions, compact, tooltipId, menuTestId }: Props) => {
  const { visible, toggleDropDown, hideDropDown } = useDropDownMenu()
  const menuRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState<CSSProperties>()

  // The dropdown is position: fixed so the table's scroll container and the panel's overflow: hidden
  // can't clip it; it opens below the gear button, or above it when there's no room below.
  useLayoutEffect(() => {
    if (!visible || !menuRef.current || !dropdownRef.current) return

    const button = menuRef.current.getBoundingClientRect()
    const gap = 8
    // clientWidth/clientHeight exclude the scrollbars that a fixed element's offsets are measured against
    const { clientWidth, clientHeight } = document.documentElement
    const opensUp = button.bottom + gap + dropdownRef.current.offsetHeight > clientHeight
    setDropdownPosition({
      right: clientWidth - button.right,
      ...(opensUp ? { bottom: clientHeight - button.top + gap } : { top: button.bottom + gap })
    })
  }, [visible])

  // A fixed menu doesn't follow its row, so close it when the page scrolls or resizes.
  useEffect(() => {
    if (!visible) return

    window.addEventListener('scroll', hideDropDown, true)
    window.addEventListener('resize', hideDropDown)
    return () => {
      window.removeEventListener('scroll', hideDropDown, true)
      window.removeEventListener('resize', hideDropDown)
    }
  }, [visible, hideDropDown])

  // Capture phase on purpose: table cells wrap this in a click-stopping handler, so a bubbling
  // listener never sees a click on another row's gear and two menus end up open at once.
  useEffect(() => {
    if (!visible) return

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) hideDropDown()
    }
    document.addEventListener('click', closeOnOutsideClick, true)
    return () => document.removeEventListener('click', closeOnOutsideClick, true)
  }, [visible, hideDropDown])

  if (!compact) {
    const desktopActions = actions.filter(action => action.showInDesktop !== false)
    return (
      <>
        {desktopActions.map(action => (
          <button
            key={action.key}
            type='button'
            className='entity-row-action-button'
            aria-label={action.label}
            onClick={action.onClick}
            data-test={action.testId}
            data-tip={action.label}
            data-for={tooltipId}
          >
            {action.icon}
          </button>
        ))}
      </>
    )
  }

  const menuActions = actions.filter(action => action.showInMenu !== false)

  const handleMenuItem = (handler: () => void) => () => {
    hideDropDown()
    handler()
  }

  return (
    <div ref={menuRef} className='entity-row-actions-menu'>
      <MMButton
        type='button'
        size='normal'
        variant='elevated'
        collapsed
        collapsedIcon={SettingsIcon}
        onClick={toggleDropDown}
        data-test={menuTestId}
      />
      <div ref={dropdownRef} className={cn('entity-row-actions-dropdown', { visible })} style={dropdownPosition}>
        {menuActions.map(action => (
          <button
            key={action.key}
            type='button'
            className='entity-row-actions-dropdown-item'
            onClick={handleMenuItem(action.onClick)}
            data-test={action.testId}
          >
            {action.menuIcon}
            {action.label}
          </button>
        ))}
      </div>
    </div>
  )
}
